import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Input,
  Heading,
  VStack,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  useColorMode,
  Card,
  CardBody,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
  Progress,
} from '@chakra-ui/react';
import { MdUpload, MdDelete, MdRefresh, MdInfo } from 'react-icons/md';
import axios from '../utils/axiosConfig';
import Navigation from '../components/Navigation';
import { Helmet } from "react-helmet";

const KnowledgeBasePage = () => {
  const { colorMode } = useColorMode();
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fileInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const fetchUploadedFiles = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get('/api/protected/knowledgebase/');
        setUploadedFiles(response.data.data);
      } catch (error) {
        console.error('Error fetching files:', error);
        setError('Failed to load knowledge base files');
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Failed to load files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUploadedFiles();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        toast({
          title: 'File too large',
          description: 'Please select a file smaller than 10MB',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a file to upload',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/api/protected/knowledgebase/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setUploadedFiles(prev => [...prev, response.data.data]);
      setFile(null);
      fileInputRef.current.value = '';
      setUploadProgress(0);

      toast({
        title: 'Success',
        description: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to upload file',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFile = async (fileId) => {
    setSelectedFile(fileId);
    onOpen();
  };

  const confirmRemoveFile = async () => {
    try {
      await axios.delete(`/api/protected/knowledgebase/${selectedFile}`);
      setUploadedFiles(prev => prev.filter(file => file._id !== selectedFile));
      onClose();
      setSelectedFile(null);

      toast({
        title: 'Success',
        description: 'File deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error removing file:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete file',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Knowledge Base | AI Call System</title>
        <meta name="description" content="Upload and manage your knowledge base files" />
      </Helmet>

      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />
        
        <Box flex="1" p={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>Knowledge Base</Heading>
            <Tooltip label="Refresh files">
              <IconButton
                icon={<MdRefresh />}
                aria-label="Refresh files"
                onClick={() => window.location.reload()}
                colorScheme="brand"
              />
            </Tooltip>
          </Flex>

          {error && (
            <Alert status="error" mb={6} borderRadius="lg">
              <AlertIcon />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card
            bg={colorMode === 'light' ? 'white' : 'gray.800'}
            borderRadius="lg"
            boxShadow="sm"
            mb={6}
          >
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md">Upload New File</Heading>
                <Text color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
                  Upload files to enhance your assistant's knowledge base. Supported formats: PDF, TXT, DOCX
                </Text>

                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={4}
                  align={{ base: 'stretch', md: 'center' }}
                >
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    accept=".pdf,.txt,.docx"
                    p={1}
                    border="1px dashed"
                    borderColor={colorMode === 'light' ? 'gray.300' : 'gray.600'}
                    _hover={{ borderColor: 'brand.500' }}
                  />
                  <Button
                    leftIcon={<MdUpload />}
                    onClick={handleUpload}
                    isLoading={loading}
                    loadingText="Uploading..."
                    colorScheme="brand"
                    isDisabled={!file}
                  >
                    Upload File
                  </Button>
                </Flex>

                {uploadProgress > 0 && (
                  <Progress
                    value={uploadProgress}
                    size="sm"
                    colorScheme="brand"
                    borderRadius="full"
                  />
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card
            bg={colorMode === 'light' ? 'white' : 'gray.800'}
            borderRadius="lg"
            boxShadow="sm"
          >
            <CardBody>
              <Heading size="md" mb={4}>Uploaded Files</Heading>

              {loading ? (
                <VStack spacing={4}>
                  {Array(3).fill(0).map((_, i) => (
                    <Skeleton key={i} height="60px" width="full" borderRadius="md" />
                  ))}
                </VStack>
              ) : uploadedFiles.length > 0 ? (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>File Name</Th>
                      <Th>Upload Date</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {uploadedFiles.map((file) => (
                      <Tr key={file._id}>
                        <Td>
                          <Flex align="center" gap={2}>
                            <Text>{file.fileName}</Text>
                          </Flex>
                        </Td>
                        <Td>
                          {new Date(file.createdAt).toLocaleDateString()}
                        </Td>
                        <Td>
                          <Tooltip label="Delete file">
                            <IconButton
                              icon={<MdDelete />}
                              aria-label="Delete file"
                              colorScheme="red"
                              size="sm"
                              onClick={() => handleRemoveFile(file._id)}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Alert status="info" borderRadius="lg">
                  <AlertIcon />
                  <Text>No files uploaded yet. Upload a file to get started.</Text>
                </Alert>
              )}
            </CardBody>
          </Card>
        </Box>
      </Flex>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete File</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>Are you sure you want to delete this file? This action cannot be undone.</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmRemoveFile}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default KnowledgeBasePage; 
